import compact from "lodash/compact";
import filter from "lodash/filter";
import first from "lodash/first";
import includes from "lodash/includes";
import isNil from "lodash/isNil";
import { BasicProfile, Profile } from "PFTypes";

import { getAttribute } from "./get_attribute";

enum ProfileRoles {
  Admin = "admin",
  SaasAdmin = "saas_admin"
}

export type ObjectWithProfileName = (
  | { firstName?: string; lastName?: string | null }
  | { firstName?: string; last_name?: string | null }
) &
  Record<string, any>;

export const getProfileName = (profile?: Partial<ObjectWithProfileName> | null | undefined): string => {
  const firstName = getAttribute(profile, "firstName") || "";
  const lastName = getAttribute(profile, "lastName") || "";

  return compact([firstName, lastName]).join(" ");
};

export const getMainPosition = (profile: Pick<Profile, "positions">) => {
  const positions = profile.positions || [];
  return filter(positions, "main")[0] || {};
};

export const getJobTitle = (profile: Pick<Profile, "positions">) => {
  const mainPosition = getMainPosition(profile);
  return first(getAttribute(mainPosition, "jobTitle"))?.value || null;
};

export const isAdmin = (profile?: Profile) => {
  if (!profile) {
    return false;
  }

  return includes(profile.roles, ProfileRoles.Admin);
};

export const isSaasAdmin = (profile?: Profile) => {
  if (!profile) {
    return false;
  }

  return includes(profile.roles, ProfileRoles.SaasAdmin);
};

export const isProfileActive = (profile?: Partial<Profile>): boolean => {
  if (!profile) {
    return false;
  }

  // We assume that profile is active in case of no data (e.g. /api/options/profiles)
  if (isNil(profile.status) && isNil(profile.active)) {
    return true;
  }

  // Current Profile doesn't have status, but has active boolean
  return profile.status === "active" || !!profile.active;
};

export const mapProfileToBasicProfile = (
  profile: Profile,
  textMapper?: (profile: Profile) => string | undefined
): BasicProfile => {
  const { id, email, avatar, firstName, lastName } = profile;
  const jobTitle = getJobTitle(profile);
  return {
    id,
    email,
    avatar,
    firstName: firstName ?? "",
    lastName: lastName ?? "",
    jobTitle: jobTitle ? String(jobTitle) : undefined,
    text: textMapper ? textMapper(profile) : undefined,
    suspendedAt: getAttribute(profile, "suspendedAt")
  };
};
